import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { tasksApi } from '@/modules/tasks/services/tasks.api';
import TaskInviteTokenService from '@/services/taskInviteToken.service';
import { Button } from '@/components/ui-v2/Button';

type Step = 'email' | 'otp' | 'redirecting';

interface OrgInfo {
    _id: string;
    name: string;
    logoKey: string | null;
}

/**
 * /review/:orgKey
 *
 * Public landing for external reviewers. Email → OTP → JWT minted → redirect
 * to ?next=... (or /review/:orgKey/tasks by default). Once a session is active,
 * other components send the invite JWT on /v2/tasks/* and /v2/reports/*.
 */
export function ReviewLandingPage() {
    const { orgKey = '' } = useParams<{ orgKey: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const next = searchParams.get('next') || `/review/${encodeURIComponent(orgKey)}/tasks`;

    const [org, setOrg] = useState<OrgInfo | null>(null);
    const [orgError, setOrgError] = useState<string | null>(null);
    const [step, setStep] = useState<Step>('email');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Resolve org name + logo for the header.
    useEffect(() => {
        let cancelled = false;
        tasksApi
            .getOrgForReview(orgKey)
            .then((info) => {
                if (cancelled) return;
                setOrg(info);
            })
            .catch(() => {
                if (cancelled) return;
                setOrgError("This review link is not valid. Check that you're using the link from the latest email.");
            });
        return () => {
            cancelled = true;
        };
    }, [orgKey]);

    // If a session already exists, skip straight to the next URL.
    useEffect(() => {
        if (TaskInviteTokenService.isActive() && TaskInviteTokenService.getOrgId()) {
            navigate(next, { replace: true });
        }
    }, [navigate, next]);

    const handleRequestOTP = useCallback(async () => {
        setError(null);
        setSubmitting(true);
        try {
            await tasksApi.requestOTP(orgKey, email);
            setStep('otp');
        } catch (e: unknown) {
            setError(humanError(e, 'Could not send the code. Try again.'));
        } finally {
            setSubmitting(false);
        }
    }, [orgKey, email]);

    const handleVerifyOTP = useCallback(async () => {
        setError(null);
        setSubmitting(true);
        try {
            const tokens = await tasksApi.verifyOTP(orgKey, email, otp);
            TaskInviteTokenService.store({
                accessToken: tokens.accessToken,
                refreshToken: tokens.refreshToken,
                expiresAt: tokens.expiresAt,
                organizationId: tokens.organizationId,
                email: tokens.email,
            });
            setStep('redirecting');
            navigate(next, { replace: true });
        } catch (e: unknown) {
            setError(humanError(e, 'That code did not match.'));
        } finally {
            setSubmitting(false);
        }
    }, [orgKey, email, otp, navigate, next]);

    return (
        <div
            className="min-h-screen flex items-center justify-center px-4"
            style={{ background: 'var(--canvas, #FAFAFA)' }}
        >
            <div
                className="w-full max-w-md p-8 rounded-2xl"
                style={{
                    background: 'var(--surface-primary, #fff)',
                    boxShadow: 'var(--shadow-lg)',
                    border: '1px solid var(--border-default)',
                }}
            >
                <div className="flex flex-col items-center mb-6">
                    {org?.logoKey ? (
                        <img
                            src={org.logoKey}
                            alt={org.name}
                            className="w-14 h-14 rounded-full mb-3"
                        />
                    ) : (
                        <div
                            className="w-14 h-14 rounded-full flex items-center justify-center mb-3"
                            style={{ background: 'var(--accent-muted)' }}
                        />
                    )}
                    <h1
                        className="text-[22px]"
                        style={{
                            fontFamily: 'var(--v2-font-serif)',
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                        }}
                    >
                        {org?.name ? `Review for ${org.name}` : 'Review'}
                    </h1>
                    <p
                        className="text-[13px] mt-1 text-center"
                        style={{ color: 'var(--text-secondary)' }}
                    >
                        Sign in to see candidates assigned to you for review.
                    </p>
                </div>

                {orgError ? (
                    <div
                        className="rounded-lg p-3 text-[13px]"
                        style={{
                            background: 'var(--bg-danger)',
                            color: 'var(--fg-danger)',
                        }}
                    >
                        {orgError}
                    </div>
                ) : step === 'email' ? (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (email.trim()) handleRequestOTP();
                        }}
                        className="flex flex-col gap-3"
                    >
                        <label className="text-[12px] font-medium" style={{ color: 'var(--text-secondary)' }}>
                            Email address
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@company.com"
                            autoComplete="email"
                            autoFocus
                            disabled={submitting}
                            className="rounded-lg px-3 py-2 text-[14px] outline-none"
                            style={{
                                border: '1px solid var(--border-default)',
                                background: 'var(--surface-secondary)',
                                color: 'var(--text-primary)',
                            }}
                        />
                        {error && (
                            <p className="text-[12.5px]" style={{ color: 'var(--fg-danger)' }}>
                                {error}
                            </p>
                        )}
                        <Button type="submit" disabled={submitting || !email.trim()} className="mt-1">
                            {submitting ? 'Sending…' : 'Send code'}
                        </Button>
                        <p
                            className="text-[11.5px] mt-2 text-center"
                            style={{ color: 'var(--text-tertiary)' }}
                        >
                            We'll send a 6-digit code. The session lasts 7 days; the code expires in 10 min.
                        </p>
                    </form>
                ) : step === 'otp' ? (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (otp.trim().length === 6) handleVerifyOTP();
                        }}
                        className="flex flex-col gap-3"
                    >
                        <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                            Code sent to <strong>{email}</strong>. Check your inbox (and spam).
                        </p>
                        <label className="text-[12px] font-medium" style={{ color: 'var(--text-secondary)' }}>
                            Verification code
                        </label>
                        <input
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="123456"
                            inputMode="numeric"
                            autoFocus
                            disabled={submitting}
                            className="rounded-lg px-3 py-2 text-[18px] tracking-[0.4em] text-center outline-none"
                            style={{
                                border: '1px solid var(--border-default)',
                                background: 'var(--surface-secondary)',
                                color: 'var(--text-primary)',
                                fontFamily: 'var(--v2-font-mono)',
                            }}
                        />
                        {error && (
                            <p className="text-[12.5px]" style={{ color: 'var(--fg-danger)' }}>
                                {error}
                            </p>
                        )}
                        <Button type="submit" disabled={submitting || otp.length < 6} className="mt-1">
                            {submitting ? 'Verifying…' : 'Sign in'}
                        </Button>
                        <button
                            type="button"
                            className="text-[12px] mt-1 underline-offset-2 hover:underline self-center"
                            style={{ color: 'var(--text-tertiary)' }}
                            onClick={() => {
                                setStep('email');
                                setOtp('');
                                setError(null);
                            }}
                        >
                            Use a different email
                        </button>
                    </form>
                ) : (
                    <p className="text-[13px] text-center" style={{ color: 'var(--text-secondary)' }}>
                        Loading your reviews…
                    </p>
                )}
            </div>
        </div>
    );
}

function humanError(err: unknown, fallback: string): string {
    const e = err as { response?: { data?: { error?: string; code?: string } }; message?: string };
    return e?.response?.data?.error || e?.message || fallback;
}

export default ReviewLandingPage;
